<script>
    import Icon from '$component/Icon.svelte';
    import Title from "$component/Title.svelte";
    import {error, items, loading, message, store as basketStore} from '$lib/basketStore.svelte.js';
    import {t, plural} from '$utils/lang.js';
    import BasketItem from "$component/BasketItem.svelte";
    import BasketSum from "$component/BasketSum.svelte";
    import ExpressPayments from "$component/ExpressPayments.svelte";
    import {buttonStyles, SuccessIcon} from "m7-library";
    import {BasketBoxIcon} from "$m7-library/main";
    import {paymentStore} from '$lib/paymentStore.svelte.js';
    import BikeInsuranceWidget from "$component/BikeInsuranceWidget.svelte";
    import OrderRemark from "$component/OrderRemark.svelte";
    import {currencyFormatter} from "$utils/formatter.js";

    let {
        status = 'active',
        onContinue = () => {},
        onActivate = () => {},
        onExpressApproved = () => {},
        // true nur auf dem Review-Step (Step 3): rendert das neue, kompakte
        // Figma-"Deine Bestellung"-Summary. User/Zahlung behalten die alte Sidebar.
        newSummary = false,
    } = $props();

    const empty = $derived(basketStore.basketEmpty);

    $effect(() => {
        if (paymentStore.expressApproved) {
            paymentStore.expressApproved = false;
            onExpressApproved();
        }
    });
</script>

<!-- reference: [1] -->
{#snippet entfernteArtikel()}
    {#if basketStore.removedItems?.length > 0}
        <div class="p-4 bg-scarlet/10 border border-scarlet/40 rounded-md text-sm" data-checkout-section="removed-items-warning">
            <span class="font-bold">{t('Artikel nicht mehr verfügbar:')}</span>
            {#each basketStore.removedItems as item}
                <div class="mt-1">– {item.title}</div>
            {/each}
        </div>
    {/if}
{/snippet}

{#if status !== 'active' && newSummary}
    <!-- Review-Step (Step 3) — compact Figma "Deine Bestellung" summary, read-only.
         No edit affordance here; editing happens via the review cards. -->
    <div class="bg-white rounded-[10px] border border-[#e5e7eb] shadow-sm p-5 flex flex-col gap-4" data-checkout-step="basket" data-checkout-state="summary">
        <span class="font-bold text-[17px] leading-6 text-[#0a0a0a]">{t("Deine Bestellung")}</span>
        {#if loading() || error() || items().length === 0}
            <div class="py-6 text-grey-dark text-xsm">{message()}</div>
        {:else}
            <!-- Mini-products list -->
            <div class="flex flex-col gap-3">
                {#each items() as item (item.id)}
                    <div class="flex items-center gap-3" data-basket-item={item.id}>
                        <img
                            src={item.image?.default?.src}
                            alt={item.name}
                            class="size-10 rounded-[4px] object-contain shrink-0 bg-white"
                        />
                        <div class="flex-1 min-w-0 flex flex-col leading-[15px]">
                            <span class="font-bold text-[11px] text-[#0a0a0a] truncate">{item.brand ? `${item.brand} ` : ''}{item.nameParts?.[0] ?? item.name}</span>
                            <span class="font-light text-[11px] text-gray-103">{t("Menge:")} {item.quantity}</span>
                        </div>
                        <span class="font-bold text-xsm text-cyprus whitespace-nowrap">{currencyFormatter.format(item.lineItemTotal)}</span>
                    </div>
                {/each}
            </div>
            <hr class="border-[#e5e7eb]">
        {/if}
        <BasketSum type="summary" class="" />
    </div>
{:else if status !== 'active'}
    <!-- Summary state (User/Zahlung steps): legacy full order overview, read-only. -->
    <div class="bg-white rounded-md border border-grey" data-checkout-step="basket" data-checkout-state="summary">
        <button
            type="button"
            onclick={onActivate}
            class="w-full flex items-center justify-between px-5 py-3.5 border-b border-grey cursor-pointer hover:bg-gray-50"
            data-checkout-action="edit-basket"
        >
            <span class="font-bold text-base">{t("Deine Bestellung")}</span>
            <Icon name="down" class="w-4 h-4 text-gray-400 shrink-0"/>
        </button>
        {#if loading() || error() || items().length === 0}
            <div class="p-10 text-grey-dark">{message()}</div>
        {:else}
            {#each items() as item (item.id)}
                <BasketItem {item} class="px-5" edit={false} showDeliveryStatus={false} />
                <hr class="border-grey" />
            {/each}
        {/if}
        <BasketSum type="checkout" class="px-5 pt-3 pb-5" />
    </div>
{:else}
    <!-- Active state: full basket UI -->
    {#if empty && !loading()}
        <div class="p-8 bg-white rounded-md border border-grey text-center my-7.5" data-checkout-step="basket" data-checkout-section="basket" data-checkout-state="empty">
            <BasketBoxIcon class="w-15 h-15 inline mb-3.5"/>
            <div class="text-lg font-bold mb-2">{t('Dein Warenkorb ist leer')}</div>
            <div class="font-light">{t('Füge Artikel hinzu, um mit dem Einkauf zu beginnen.')}</div>
        </div>
        {@render entfernteArtikel()}
    {:else}
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-4" data-checkout-step="basket" data-checkout-state="filled">
        <div class="flex flex-col gap-4 lg:flex-1 lg:min-w-0">
        <div class="flex flex-col gap-2 rounded-md" data-basket-count={items().length}>
            <Title icon="basket" title={t("Dein Warenkorb")} class="px-5 py-3.5 bg-white rounded-md border border-grey shadow-sm"/>
            {#if loading() || error() || items().length === 0}
                <div class="p-10 bg-white text-grey-dark border border-grey rounded-md shadow-sm">{message()}</div>
            {:else}
                {#each items() as item (item.id)}
                    <BasketItem {item} class="rounded-md border border-grey shadow-sm" edit={true} showDeliveryStatus={true}/>
                {/each}
            {/if}
        </div>
        {@render entfernteArtikel()}
        {#if basketStore.hasAkkuMix}
            <div class="p-4 bg-scarlet/10 border border-scarlet/40 rounded-md text-sm" data-checkout-section="akku-mix-warning">
                <span class="font-bold">{t('Hinweis:')}</span>
                {t('Akkus können nicht zusammen mit anderen Artikeln bestellt werden. Bitte entferne entweder die Akkus oder alle anderen Artikel aus deinem Warenkorb.')}
            </div>
        {/if}
        {#if basketStore.hasMultipleDeliveryTimes}
            <div class="p-4 bg-orange/10 border border-orange/30 rounded-md text-sm" data-checkout-section="delivery-warning" data-basket-max-delivery-days={basketStore.maxDeliveryDays}>
                <span class="font-bold">{t('Bitte beachte:')}</span>
                <!-- reference: [m7_library Utils/i18n 2] -->
                {t(plural(basketStore.maxDeliveryDays, {
                    one: 'Deine Bestellung wird voraussichtlich in {days} Tag versendet, da die Artikel zu unterschiedlichen Zeiten versandfertig sind und wir alles zusammen verschicken.',
                    other: 'Deine Bestellung wird voraussichtlich in {days} Tagen versendet, da die Artikel zu unterschiedlichen Zeiten versandfertig sind und wir alles zusammen verschicken.',
                }), {days: basketStore.maxDeliveryDays})}
            </div>
        {/if}
        <BikeInsuranceWidget/>
        <OrderRemark/>
        </div>
        <div class="flex flex-col gap-4 lg:w-95 lg:shrink-0 lg:sticky lg:top-4">
        <div class="flex flex-col gap-3.5 rounded-md shadow-sm">
            <BasketSum type="basket"/>
        </div>
        <div class="flex flex-col gap-3.5 rounded-md shadow-sm" data-checkout-section="checkout-actions">
            <div class="p-3.5 bg-white rounded-md border border-grey">
                <!-- CSS-Overlay-Pattern (siehe m7_bike_insurance Legacy):
                     ein pseudo-element ::before deckt das gesamte Express-
                     Container ab solange Insurance-Form invalid ist. Cross-
                     origin iframe-Clicks (PayPal) erreichen den Inhalt nicht
                     mehr. Klick auf das Overlay triggert Validation und macht
                     Errors sichtbar. -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <div
                    class:pointer-events-none={basketStore.hasAkkuMix}
                    class:opacity-50={basketStore.hasAkkuMix}
                    class:m7-insurance-invalid={basketStore.insurance?.active && !basketStore.insurance?.valid}
                    class="m7-next-step-container relative"
                    onclick={() => {
                        if (window.__m7_validate_insurance) window.__m7_validate_insurance();
                    }}
                >
                    <!--
                        Wrapper-Div ist absichtlich kein <button>: enthält PayPal-iframes
                        und Amazon-Pay-Custom-Element. Klick-Listener triggert
                        Validation des Bike-Insurance-Forms via CSS-Overlay-Pattern
                        (siehe m7_bike_insurance Legacy). Keyboard-Aktivierung erfolgt
                        ohnehin über den enthaltenen Express-Buttons der Provider.
                    -->
                    <ExpressPayments/>
                </div>
                <div class="pt-4.5 pb-3 font-light text-center">
                    {t('oder')}
                </div>
                <button
                    class={buttonStyles['elm-button']}
                    disabled={basketStore.hasAkkuMix}
                    class:opacity-50={basketStore.hasAkkuMix}
                    class:cursor-not-allowed={basketStore.hasAkkuMix}
                    onclick={() => {
                        if (window.__m7_validate_insurance && !window.__m7_validate_insurance()) return;
                        onContinue();
                    }}
                    data-checkout-action="start-checkout"
                >
                    {t('Sicher zur Kasse gehen')}
                </button>
                <hr class="mt-4.5 mb-4 border-grey"/>
                <div class="flex flex-col gap-2 text-cyprus">
                    <div class="flex items-center gap-2 text-xs font-bold tracking-widest">
                        <SuccessIcon class="h-4 w-4"/>
                        {t('schneller Versand')}
                    </div>
                    <div class="flex items-center gap-2 text-xs font-bold tracking-widest">
                        <SuccessIcon class="h-4 w-4"/>
                        {t('Overnight Express Lieferung möglich')}
                    </div>
                    <div class="flex items-center gap-2 text-xs font-bold tracking-widest">
                        <SuccessIcon class="h-4 w-4"/>
                        {t('fahrfertig montiert')}
                    </div>
                    <div class="flex items-center gap-2 text-xs font-bold tracking-widest">
                        <SuccessIcon class="h-4 w-4"/>
                        {t('100 Tage Rückgabegarantie')}
                    </div>
                </div>
                <hr class="mt-4.5 mb-4 border-grey"/>
                <div class="text-center font-light text-xs">
                    {t('Sichere Verschlüsselung & 100% Datenschutz')}
                </div>
            </div>
        </div>
        </div>
        </div>
    {/if}
{/if}

<style>
    /* Overlay über Express-Buttons-Container wenn Insurance-Form invalid.
       Catches cross-origin iframe clicks (PayPal SDK) by sitting on top. */
    .m7-next-step-container.m7-insurance-invalid::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1000;
        cursor: pointer;
    }
</style>
